import React, { useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import GlassCard from './GlassCard';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || '';

const Login = () => {
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showLocalAuth, setShowLocalAuth] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [familyCode, setFamilyCode] = useState('');
  const [error, setError] = useState('');

  // Check for family code in URL
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setFamilyCode(codeFromUrl.toUpperCase());
      setShowLocalAuth(true);
      setIsSignUp(true);
    }
  }, [searchParams]);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        
        if (userInfoResponse.ok) {
          const userInfo = await userInfoResponse.json();
          login(tokenResponse.access_token, userInfo);
          navigate('/');
        } else {
          throw new Error('Failed to fetch user info');
        }
      } catch (error) {
        console.error('Login error:', error);
        alert('Failed to complete login. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setLoading(false);
      alert('Login failed. Please try again.');
    },
    flow: 'implicit',
    scope: 'openid email profile',
  });

  const handleGitHubLogin = () => {
    if (!GITHUB_CLIENT_ID) {
      alert('GitHub authentication is not configured.');
      return;
    }
    
    setLoading(true);
    const redirectUri = `${window.location.origin}/login`;
    const scope = 'read:user user:email';
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('github_oauth_state', state);
    
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}`;
    window.location.href = githubAuthUrl;
  };

  const handleLocalAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_FTAPI_URL || 'https://ftapi.twodots.cloud';
      const API_KEY = process.env.REACT_APP_FTAPI_API_KEY || '';
      
      // Handle both login and sign-up
      const endpoint = isSignUp ? '/api/ft/auth/register' : '/api/ft/auth/login';
      const body = isSignUp 
        ? { 
            email, 
            password, 
            name: name.trim() || undefined,
            family_code: familyCode.trim() || undefined
          }
        : { email, password };

      console.log('Attempting login to:', `${API_BASE_URL}${endpoint}`);
      console.log('API Key present:', !!API_KEY);
      console.log('Request body:', { ...body, password: '***' });
      
      let response;
      try {
        response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(API_KEY && { 'x-api-key': API_KEY }),
          },
          body: JSON.stringify(body),
          mode: 'cors', // Explicitly set CORS mode
          credentials: 'include', // Include cookies for CORS
        });
      } catch (fetchError) {
        console.error('Fetch error details:', {
          name: fetchError.name,
          message: fetchError.message,
          stack: fetchError.stack,
          cause: fetchError.cause,
        });
        // Check if it's a CORS error
        if (fetchError.message.includes('CORS') || fetchError.message.includes('Access-Control')) {
          throw new Error('CORS error: The server is not allowing requests from this origin. Please contact support.');
        }
        // Check if it's a network error
        if (fetchError.message === 'Failed to fetch' || fetchError.name === 'TypeError') {
          throw new Error(`Network error: Unable to connect to ${API_BASE_URL}. Please check your internet connection and try again.`);
        }
        throw new Error(`Network error: ${fetchError.message}. Please check if ${API_BASE_URL} is accessible.`);
      }

      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Login successful, received data:', { ...data, access_token: data.access_token ? '***' : null });

      login(data.access_token, data.user);
      
      // If family code was provided and user just signed up, try to join the family
      if (isSignUp && familyCode.trim()) {
        try {
          const API_BASE_URL = process.env.REACT_APP_FTAPI_URL || 'https://ftapi.twodots.cloud';
          const API_KEY = process.env.REACT_APP_FTAPI_API_KEY || '';
          
          const joinResponse = await fetch(`${API_BASE_URL}/api/ft/families/join`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.access_token}`,
              ...(API_KEY && { 'x-api-key': API_KEY }),
            },
            body: JSON.stringify({ join_code: familyCode.trim().toUpperCase() }),
          });
          
          if (joinResponse.ok) {
            const joinData = await joinResponse.json();
            if (joinData.family_id) {
              navigate(`/families/${joinData.family_id}`);
              return;
            }
          }
        } catch (joinErr) {
          console.error('Error joining family:', joinErr);
          // Continue to home page even if join fails
        }
      }
      
      navigate('/');
    } catch (error) {
      console.error('Local auth error:', error);
      // Handle network errors
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        setError('Unable to connect to server. Please check your connection and try again.');
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle GitHub OAuth callback
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const storedState = sessionStorage.getItem('github_oauth_state');

    if (code && state && state === storedState) {
      sessionStorage.removeItem('github_oauth_state');
      handleGitHubCallback(code);
    }
  }, []);

  const handleGitHubCallback = async (code) => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_FTAPI_URL || 'https://ftapi.twodots.cloud';
      const API_KEY = process.env.REACT_APP_FTAPI_API_KEY || '';
      
      const response = await fetch(`${API_BASE_URL}/api/ft/auth/github`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY && { 'x-api-key': API_KEY }),
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to exchange code for token');
      }

      const data = await response.json();
      login(data.access_token, data.user);
      navigate('/');
    } catch (error) {
      console.error('GitHub login error:', error);
      alert('Failed to complete GitHub login. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasAnyAuth = GOOGLE_CLIENT_ID || GITHUB_CLIENT_ID || true; // Always show local auth

  if (!hasAnyAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <GlassCard style={{ textAlign: 'center', padding: '48px', maxWidth: '400px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '16px', color: theme.text.primary }}>
            Authentication Not Configured
          </h2>
          <p style={{ color: theme.text.secondary }}>
            Please configure authentication providers in the environment variables.
          </p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', padding: '16px' }}>
      <GlassCard style={{ textAlign: 'center', padding: 'clamp(24px, 5vw, 48px)', maxWidth: '450px', width: '100%' }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/assets/family-tree-logo.png"
            alt="Family Tree"
            style={{
              height: 'clamp(60px, 10vw, 80px)',
              width: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
        <h1 style={{ fontSize: 'clamp(24px, 5vw, 32px)', marginBottom: '12px', color: theme.text.primary }}>
          Family Tree
        </h1>
        <p style={{ color: theme.text.secondary, marginBottom: '24px', fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
          {showLocalAuth && isSignUp ? 'Create an account to start building your family tree' : 'Sign in to access your family tree'}
        </p>

        {!showLocalAuth ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Google sign-in hidden for now */}
            {false && GOOGLE_CLIENT_ID && (
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                style={{
                  padding: '14px 28px',
                  borderRadius: '8px',
                  border: 'none',
                  background: loading ? theme.text.muted : '#4285F4',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  width: '100%',
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = '#357ae8';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(66, 133, 244, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = '#4285F4';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {loading ? 'Signing in...' : 'Sign in with Google'}
              </button>
            )}

            {GITHUB_CLIENT_ID && (
              <button
                onClick={handleGitHubLogin}
                disabled={loading}
                style={{
                  padding: '14px 28px',
                  borderRadius: '8px',
                  border: 'none',
                  background: loading ? theme.text.muted : '#24292e',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  width: '100%',
                  opacity: loading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = '#1a1e22';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(36, 41, 46, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = '#24292e';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                {loading ? 'Signing in...' : 'Sign in with GitHub'}
              </button>
            )}

            {(GITHUB_CLIENT_ID) && (
              <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ flex: 1, height: '1px', background: theme.glass.border }}></div>
                <span style={{ color: theme.text.muted, fontSize: '14px' }}>or</span>
                <div style={{ flex: 1, height: '1px', background: theme.glass.border }}></div>
              </div>
            )}

            <button
              onClick={() => setShowLocalAuth(true)}
              disabled={loading}
              style={{
                padding: '14px 28px',
                borderRadius: '8px',
                border: `1px solid ${theme.glass.border}`,
                background: 'transparent',
                color: theme.text.primary,
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                width: '100%',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = theme.card.background;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              Sign in with Email
            </button>
          </div>
        ) : (
          <form onSubmit={handleLocalAuth} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {error && (
              <div style={{
                padding: '12px',
                borderRadius: '8px',
                background: 'rgba(255, 0, 0, 0.1)',
                border: '1px solid rgba(255, 0, 0, 0.3)',
                color: '#ff4444',
                fontSize: '14px',
              }}>
                {error}
              </div>
            )}

            {/* Name field for sign-up */}
            {isSignUp && (
              <input
                type="text"
                placeholder="Full Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
            )}

            {/* Family Code field for sign-up */}
            {isSignUp && (
              <input
                type="text"
                placeholder="Family Code (optional - enter 8-character code to join a family)"
                value={familyCode}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                  setFamilyCode(value);
                }}
                maxLength={8}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  width: '100%',
                  boxSizing: 'border-box',
                  fontFamily: 'monospace',
                  letterSpacing: '2px',
                }}
              />
            )}

            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                fontSize: '16px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                border: theme.glass.border,
                background: theme.card.background,
                color: theme.text.primary,
                fontSize: '16px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '14px 28px',
                borderRadius: '8px',
                border: 'none',
                background: loading ? theme.text.muted : theme.accent.primary,
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                width: '100%',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowLocalAuth(false);
                  setError('');
                  setEmail('');
                  setPassword('');
                  setName('');
                  setFamilyCode('');
                  setIsSignUp(false);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme.text.secondary,
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setFamilyCode('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme.accent.primary,
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </button>
            </div>
          </form>
        )}

        {loading && !showLocalAuth && (
          <p style={{ color: theme.text.muted, fontSize: '14px', marginTop: '8px' }}>
            Please wait...
          </p>
        )}
      </GlassCard>
    </div>
  );
};

export default Login;
